import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner, StatCard, PeriodFilter } from './core';

export function AnalyticsTab({ analytics, viewsData, theme, statsDays = 0, setStatsDays }) {
  const light = theme === 'light';
  const [geo, setGeo] = useState([]);
  const [details, setDetails] = useState(null);
  const [ratingStats, setRatingStats] = useState({});
  useEffect(() => {
    axios.get(`${API}/admin/geodata`, { withCredentials: true }).then(r => setGeo(r.data)).catch(() => {});
    axios.get(`${API}/admin/analytics/contents-details`, { withCredentials: true }).then(r => setDetails(r.data)).catch(() => {});
    axios.get(`${API}/admin/ratings-stats`, { withCredentials: true }).then(r => setRatingStats(r.data || {})).catch(() => {});
  }, []);

  const fmtDur = (secs) => {
    const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); const s = secs % 60;
    return `${h}h ${m.toString().padStart(2, '0')}min ${s.toString().padStart(2, '0')}s`;
  };

  if (!analytics) return <LoadingSpinner />;
  const chartData = viewsData.slice(-(statsDays > 0 ? statsDays : 30));
  const maxV = Math.max(1, ...chartData.map(d => d.views));
  const periodLabel = statsDays > 0 ? `${statsDays} derniers jours` : 'Depuis le début';
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Analytics avancé</h1>
        {setStatsDays && <PeriodFilter days={statsDays} setDays={setStatsDays} light={light} />}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Eye} label="Vues totales" value={analytics.total_views} color="text-purple-400" bgColor="bg-purple-500/10" sub={periodLabel} theme={theme} />
        <StatCard icon={Film} label="Films" value={analytics.total_movies} theme={theme} />
        <StatCard icon={Tv} label="Séries" value={analytics.total_series} color="text-blue-400" bgColor="bg-blue-500/10" theme={theme} />
        <StatCard icon={TrendingUp} label="Taux complétion" value="72%" color="text-green-400" bgColor="bg-green-500/10" theme={theme} />
      </div>
      {Object.keys(ratingStats).length > 0 && (() => {
        const entries = Object.entries(ratingStats).filter(([, s]) => s.title);
        const loved = entries.filter(([, s]) => s.score > 0).sort((a, b) => b[1].score - a[1].score).slice(0, 5);
        const hated = entries.filter(([, s]) => s.dislike > 0).sort((a, b) => b[1].dislike - a[1].dislike || a[1].score - b[1].score).slice(0, 5);
        const Row = ({ s, neg }) => (
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2 min-w-0">
              {s.thumbnail_url && <img src={s.thumbnail_url} alt="" className="w-10 h-6 object-cover rounded flex-none" />}
              <span className={`text-xs font-medium truncate ${light ? 'text-gray-800' : 'text-white/85'}`}>{s.title}</span>
            </div>
            <span className={`text-xs font-bold flex-none ml-3 ${neg ? 'text-red-400' : 'text-emerald-400'}`}>
              {neg ? `${s.dislike} 👎` : `❤ ${s.love} · 👍 ${s.like}`}
            </span>
          </div>
        );
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6" data-testid="ratings-panels">
            <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`} data-testid="ratings-loved">
              <h3 className={`text-sm font-semibold mb-2 ${light ? 'text-gray-800' : 'text-white'}`}>Contenus les plus aimés</h3>
              {loved.length === 0 && <p className={`text-xs ${light ? 'text-gray-500' : 'text-white/40'}`}>Aucun avis positif pour le moment.</p>}
              {loved.map(([cid, s]) => <Row key={cid} s={s} />)}
            </div>
            <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`} data-testid="ratings-hated">
              <h3 className={`text-sm font-semibold mb-2 ${light ? 'text-gray-800' : 'text-white'}`}>Contenus les moins appréciés</h3>
              {hated.length === 0 && <p className={`text-xs ${light ? 'text-gray-500' : 'text-white/40'}`}>Aucun avis négatif pour le moment.</p>}
              {hated.map(([cid, s]) => <Row key={cid} s={s} neg />)}
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Engagement ({statsDays > 0 ? `${statsDays}j` : '30j'})</h3>
          <div className="flex items-end gap-[2px] h-40">
            {chartData.map((d, i) => (
              <div key={i} className="flex-1 bg-[#E9641A]/30 hover:bg-[#E9641A]/60 rounded-t transition-colors" title={`${d.date} · ${d.views} vue${d.views > 1 ? 's' : ''}`} style={{ height: `${Math.max(3, (d.views / maxV) * 100)}%` }} />
            ))}
          </div>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Globe size={14} className="text-[#E9641A]" /> Données géographiques</h3>
          <div className="space-y-2">
            {geo.slice(0, 8).map((g, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`text-xs w-28 truncate ${light ? 'text-gray-500' : 'text-white/50'}`}>{g.country}</span>
                <div className={`flex-1 h-2 rounded-full ${light ? 'bg-gray-100' : 'bg-white/5'}`}><div className="h-full bg-[#229FA1] rounded-full" style={{ width: `${(g.views / (geo[0]?.views || 1)) * 100}%` }} /></div>
                <span className={`text-[10px] w-12 text-right ${light ? 'text-gray-400' : 'text-white/35'}`}>{g.views}</span>
                <span className={`text-[10px] w-8 text-right ${light ? 'text-gray-300' : 'text-white/25'}`}>{g.users}u</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {details && (
        <div className={`rounded-xl p-5 border mb-6 ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`} data-testid="analytics-contents-details">
          <h3 className={`text-sm font-semibold mb-4 ${light ? 'text-gray-800' : 'text-white'}`}>Détail des contenus</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
            {[
              ['Total contenus', details.counts.total, 'text-[#E9641A]'],
              ['Séries', details.counts.series, 'text-blue-400'],
              ['Épisodes', details.counts.episodes, 'text-cyan-400'],
              ['Fictions', details.counts.fiction, 'text-purple-400'],
              ['Documentaires', details.counts.documentaire, 'text-green-400'],
              ['Jeunesse', details.counts.jeunesse, 'text-pink-400'],
              ['Jeux', details.counts.game, 'text-yellow-400'],
            ].map(([lbl, val, color]) => (
              <div key={lbl} className={`rounded-lg p-3 text-center ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
                <p className={`text-xl font-bold ${color}`}>{val}</p>
                <p className={`text-[10px] uppercase tracking-wide ${light ? 'text-gray-400' : 'text-white/35'}`}>{lbl}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-lg p-4 ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
            <p className={`text-[10px] uppercase tracking-wide mb-2 ${light ? 'text-gray-400' : 'text-white/35'}`}>Durée cumulée des contenus (films + épisodes)</p>
            <p className={`text-lg font-bold ${light ? 'text-gray-900' : 'text-white'}`} data-testid="analytics-total-duration">{fmtDur(details.duration.total_seconds)}</p>
            <p className={`text-[11px] mt-1 ${light ? 'text-gray-500' : 'text-white/45'}`}>
              soit {details.duration.total_hours.toLocaleString('fr-FR')} heures · {details.duration.total_minutes.toLocaleString('fr-FR')} minutes · {details.duration.total_seconds.toLocaleString('fr-FR')} secondes
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              {Object.entries(details.duration_by_type).filter(([, v]) => v > 0).map(([t, v]) => (
                <span key={t} className={`text-[10px] ${light ? 'text-gray-400' : 'text-white/35'}`}>{TYPE_LABELS_PLURAL[t] || t} : {fmtDur(v)}</span>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Top contenus</h3>
        {analytics.popular_contents?.map((c, i) => (
          <div key={i} className={`flex items-center gap-2 py-1.5 text-xs border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
            <span className={`w-4 text-right ${light ? 'text-gray-300' : 'text-white/20'}`}>{i + 1}</span>
            <span className={`flex-1 truncate ${light ? 'text-gray-700' : 'text-white'}`}>{c.title}</span>
            <span className={`${light ? 'text-gray-400' : 'text-white/35'}`}>{(c.views || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
      <LanguageStats theme={theme} />
    </div>
  );
}

